import { BigDecimal } from "@solomon-labs/bigdecimal";
import type { JupiterSwapExecuteResponse } from "@solomon-labs/swap-client";
import { useEffect, useRef, useState } from "react";

import type { MigrationAttemptRecord } from "@/services/usdv-migration/analytics";
import { type IntermediateUsdc, readIntermediateUsdc, saveIntermediateUsdc } from "@/services/usdv-migration/storage";
import {
    confirmedUsdcAmount,
    createPendingMigrationSwap,
    type PendingMigrationSwap,
    recoverIntermediateUsdc,
    recoverMigrationSwap
} from "@/services/usdv-migration/swapRecovery";

interface RecoveryResult {
    failed: boolean;
    stillPending?: boolean;
    pending?: PendingMigrationSwap;
}

interface MigrationSwapRecovery {
    record: IntermediateUsdc;
    pending: boolean;
    prepare: (
        bytes: Uint8Array,
        leg: PendingMigrationSwap["leg"],
        inputAmount: string,
        remainingLegacyUsdv: string,
        analytics?: MigrationAttemptRecord
    ) => Promise<void>;
    complete: (receipt: JupiterSwapExecuteResponse) => void;
    recover: () => Promise<RecoveryResult>;
}

/** Owns durable swap state; an uncertain submission can only be checked, never replaced. */
export function useMigrationSwapRecovery(owner: string | null): MigrationSwapRecovery {
    const ownerRef = useRef(owner);
    ownerRef.current = owner;
    const [stored, setStored] = useState(() => ({ owner, record: readIntermediateUsdc(owner) }));
    useEffect(() => {
        setStored({ owner, record: readIntermediateUsdc(owner) });
    }, [owner]);
    const record = stored.owner === owner ? stored.record : readIntermediateUsdc(owner);
    const save = (requestOwner: string, value: IntermediateUsdc): void => {
        saveIntermediateUsdc(requestOwner, value);
        if (ownerRef.current === requestOwner) setStored({ owner: requestOwner, record: value });
    };
    const applyDelta = (requestOwner: string, signature: string, delta: BigDecimal): void => {
        const latest = readIntermediateUsdc(requestOwner);
        if (latest.pendingSwap?.signature !== signature && latest.unresolvedSignature !== signature) return;
        const amount = new BigDecimal(latest.amount).add(delta);
        if (amount.lt(0)) throw new Error("The swap spent more than the recorded migration proceeds.");
        save(requestOwner, { amount: amount.toString() });
    };
    return {
        record,
        pending: !!record.pendingSwap || !!record.unresolvedSignature,
        prepare: async (bytes, leg, inputAmount, remainingLegacyUsdv, analytics): Promise<void> => {
            if (!owner || ownerRef.current !== owner) throw new Error("Wallet changed. Review the migration again.");
            const latest = readIntermediateUsdc(owner);
            if (latest.pendingSwap || latest.unresolvedSignature)
                throw new Error("Check the previous swap before continuing.");
            const pendingSwap = await createPendingMigrationSwap(bytes, leg, inputAmount, remainingLegacyUsdv);
            if (ownerRef.current !== owner) throw new Error("Wallet changed. Review the migration again.");
            save(owner, { amount: latest.amount, pendingSwap: { ...pendingSwap, analytics } });
        },
        complete: (receipt): void => {
            if (!owner) return;
            const pending = readIntermediateUsdc(owner).pendingSwap;
            if (!pending) return;
            if (pending.searchFromSlot === undefined && receipt.signature !== pending.signature)
                throw new Error("The swap receipt has a different transaction ID.");
            const delta =
                pending.leg === "legacy-to-usdc"
                    ? confirmedUsdcAmount(receipt)
                    : new BigDecimal(pending.inputAmount).divide(BigDecimal.pow10(6)).multiply(-1);
            if (delta) applyDelta(owner, pending.signature, delta);
        },
        recover: async (): Promise<RecoveryResult> => {
            if (!owner) throw new Error("Connect the wallet used for this migration.");
            const latest = readIntermediateUsdc(owner);
            if (latest.pendingSwap) {
                const pending = latest.pendingSwap;
                const resolution = await recoverMigrationSwap(owner, pending);
                if (resolution.status === "pending") return { failed: false, stillPending: true, pending };
                if (resolution.status === "failed") {
                    if (readIntermediateUsdc(owner).pendingSwap?.signature === pending.signature)
                        save(owner, { amount: latest.amount });
                    return { failed: true, pending };
                }
                applyDelta(owner, pending.signature, resolution.usdcDelta);
                return { failed: false, pending };
            }
            if (latest.unresolvedSignature) {
                applyDelta(
                    owner,
                    latest.unresolvedSignature,
                    await recoverIntermediateUsdc(owner, latest.unresolvedSignature)
                );
            }
            return { failed: false };
        }
    };
}
